import { Button, Dialog, Empty, Grid, Input, Popup, SearchBar, Selector, Toast, Stepper } from 'antd-mobile';
import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import API_ROUTES from '../../api';
import {
    CARD_EVENT_IDS,
    EVENT_DESCRIPTIONS,
    GOAL_RECORD_EVENT_IDS,
    HAPPY_EVENT_ID,
    isGoalRecordEvent
} from '../../features/match/model/gameEvents';
import Icon from '../../icon';
import './index.css';

const EventInfo = ({ eventInfo, gameInfo, getGameDetail, memberListInfo, getEventInfo }) => {
    const navigate = useNavigate();

    const memberInfo = JSON.parse(localStorage.getItem("memberInfo"))

    const [eventAlbumMap, setEventAlbumMap] = useState({});
    const [selectedEventAlbum, setSelectedEventAlbum] = useState(null);
    const [addRecordVisible, setAddRecordVisible] = useState(false);
    const [addRecordType, setAddRecordType] = useState('');
    const [addRecordData, setAddRecordData] = useState({});
    const [editRecordVisible, setEditRecordVisible] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [editRecordData, setEditRecordData] = useState({});
    const [eventSearchValue, setEventSearchValue] = useState('');
    const [showSubstitutionRecords, setShowSubstitutionRecords] = useState(true);
    const [allMemberOptions, setAllMemberOptions] = useState([]);
    const [happyMemberPopupVisible, setHappyMemberPopupVisible] = useState(false);
    const [happyMemberSearch, setHappyMemberSearch] = useState('');
    const [happyMemberDraft, setHappyMemberDraft] = useState([]);
    const [happyMemberPickerTarget, setHappyMemberPickerTarget] = useState('add');

    const others = memberListInfo?.filter(user => user.gameType === 1
        || user.gameType === 2
        || user.gameType === 3);

    const getMediaUrl = (item) => item?.mediaUrl || item?.media_url || item?.url || '';

    const getMediaType = (item) => Number(item?.mediaType ?? item?.media_type);

    const getMediaEventId = (item) => item?.eventId || item?.event_id;
    // 兼容新旧接口返回格式，确保上下场人员字段始终按逗号字符串读取。
    const getUpDownMemberId = (item) => item?.upDownMemberId ?? item?.up_down_member_id ?? '';

    const happyQuickOptions = ['百场', '生日', '帽子戏法','大四喜'];
    // 上下场事件统一使用 17；18 是暂停事件，不能混入人员轮换编辑。
    const SUBSTITUTION_EVENT_ID = '17';

    const goalTypeOptions = [
        { label: '运动战(左脚)', value: '13' }, { label: '运动战(右脚)', value: '14' },
        { label: '运动战(头球)', value: '11' }, { label: '运动战(其他)', value: '3' },
        { label: '乌龙球', value: '4' }, { label: '点球未进', value: '9' },
        { label: '任意球(左脚)', value: '19' }, { label: '任意球(右脚)', value: '15' },
        { label: '角球(左脚)', value: '20' }, { label: '角球(右脚)', value: '16' },
        { label: '点球(左脚)', value: '21' }, { label: '点球(右脚)', value: '12' },
    ];

    const cardTypeOptions = [
        { label: '黄牌', value: '5' },
        { label: '红牌', value: '6' },
    ];

    const buildThumbUrl = (item) => {
        const url = getMediaUrl(item);
        if (!url) return '';
        // 赛况里只展示缩略预览，减少图片和视频打开前的加载压力。
        if (getMediaType(item) === 2) {
            return `${url}${url.includes('?') ? '&' : '?'}x-oss-process=video/snapshot,t_10000,m_fast`;
        }
        return url + Icon.ImageFooter;
    };

    useEffect(() => {
        if (!gameInfo?.id) {
            setEventAlbumMap({});
            return;
        }

        axios.get(API_ROUTES.album_list, {
            params: {
                current: 1,
                size: 200,
                gameId: gameInfo.id,
                status: 0,
                currentMemberId: memberInfo?.id
            }
        }).then((response) => {
            const records = response.data?.data?.records || response.data?.data || [];
            const nextMap = {};
            (Array.isArray(records) ? records : []).forEach(item => {
                const bindEventId = getMediaEventId(item);
                if (!bindEventId) return;
                const key = String(bindEventId);
                nextMap[key] = [...(nextMap[key] || []), item];
            });
            setEventAlbumMap(nextMap);
        }).catch(() => {
            setEventAlbumMap({});
        });
    }, [gameInfo?.id, memberInfo?.id]);

    const selectedEventMediaList = useMemo(() => selectedEventAlbum?.mediaList || [], [selectedEventAlbum]);

    const recordMediaViews = (mediaList = []) => {
        // 比赛详情赛况弹窗打开影像时，为当前展示的图片/视频增加一次浏览次数。
        const mediaIds = [...new Set(mediaList.map(media => media?.id).filter(Boolean))];
        if (!mediaIds.length) return;
        Promise.all(mediaIds.map(mediaId => (
            axios.post(API_ROUTES.album_view, null, { params: { mediaId } }).catch(() => null)
        )));
    };

    const openEventAlbum = (event, mediaList) => {
        if (!mediaList?.length) return;
        recordMediaViews(mediaList);
        setSelectedEventAlbum({ event, mediaList });
    };

    // 增加颜色精度与渐变效果
    const getTeamDesignTokens = (teamColor) => {
        const baseColor = teamColor === 'green' ? '#00B42A' : (teamColor === 'red' ? '#F53F3F' : (teamColor || '#165DFF'));

        return {
            '--team-color': baseColor,
            '--team-light': `${baseColor}1F`, // 约 12% 透明度
            '--team-gradient': `linear-gradient(135deg, ${baseColor} 0%, ${baseColor}EE 100%)`,
            '--team-shadow': `rgba(0,0,0,0.12)`
        };
    };


    const formatEventDescription = (description) => {
        if (!description) return null;

        // 匹配括号内容
        const match = description.match(/^(.+?)\((.+?)\)$/);

        if (match) {
            // 有括号的情况：运动战(左脚)
            return {
                main: match[1],      // 运动战
                detail: match[2]     // 左脚
            };
        } else {
            // 没有括号的情况：乌龙球
            return {
                main: description,
                detail: null
            };
        }
    };

    const teamInfo = (item, position, team1Goals, team2Goals) => {
        const isLeft = position === 'left';
        const teamColor = isLeft ? gameInfo?.greenColor : gameInfo?.redColor;
        const tokens = getTeamDesignTokens(teamColor);

        const isSubstitution = ["17", "18"].includes(String(item.eventId));

        const eventIdStr = String(item.eventId);
        const isGoalEvent = isGoalRecordEvent(eventIdStr);

        // 核心对齐逻辑：左队靠右排，右队靠左排，完美对称
        const alignmentStyle = {
            textAlign: isLeft ? 'right' : 'left',
            display: 'flex',
            flexDirection: isLeft ? 'row-reverse' : 'row',
            alignItems: 'center',
            justifyContent: isLeft ? 'flex-start' : 'flex-start', // 这里统一用 flex-start，因为 row-reverse 已经调换了起点
        };

        // 进球详情行的特殊对齐（包含胶囊）
        const descRowStyle = {
            display: 'flex',
            justifyContent: isLeft ? 'flex-end' : 'flex-start',
            marginTop: '2px'
        };

        return isGoalEvent ? (
            <div
                className="goal-card-container"
                style={{ ...tokens, alignSelf: isLeft ? 'flex-end' : 'flex-start' }}
            >
                <div className="goal-card-score-header">{team1Goals}:{team2Goals}</div>
                <div className="goal-card-content-body">
                    {/* 球员与图标 */}
                    <div style={{ ...alignmentStyle, gap: '4px' }}>
                        <span className="event-icon-bg">
                            <img src={eventIdStr === "4" ? Icon.OWN_GOAL : (["12", "21"].includes(eventIdStr) ? Icon.PENALTY_KICK : (eventIdStr === "9" ? Icon.PENALTY_NOT_SCORED : Icon.GOAL))} style={{ width: '11px' }} alt="" />
                        </span>
                        <span className="event-player-name">{item.userName}</span>
                    </div>
                    {/* 助攻信息 */}
                    {item.assistMemberName && (
                        <div className="event-assist-row" style={{ ...alignmentStyle, gap: '3px', marginTop: '2px' }}>
                            <img src={Icon.ASSIST} style={{ width: '10px' }} alt="助攻" />
                            <span>{item.assistMemberName}</span>
                        </div>
                    )}
                    {/* 运动战/定位球等详情 */}
                    {EVENT_DESCRIPTIONS[item.eventId] && (() => {
                        const eventDesc = formatEventDescription(EVENT_DESCRIPTIONS[item.eventId]);
                        return (
                            <div style={descRowStyle}>
                                <div className="event-desc-pill">
                                    <span>{eventDesc.main}</span>
                                    {eventDesc.detail && (
                                        <span className="event-desc-detail" style={{ marginLeft: isLeft ? '0' : '2px', marginRight: isLeft ? '2px' : '0' }}>
                                            {/* 左侧队伍靠右展示，细节连接符放在左边，避免读成“右脚·”。 */}
                                            {`·${eventDesc.detail}`}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })()}
                </div>
            </div>
        ) : (
            <div
                className={`event-card ${isLeft ? 'event-card-left' : 'event-card-right'}`}
                style={{ ...tokens, alignSelf: isLeft ? 'flex-end' : 'flex-start' }}
            >
                {!isSubstitution && (
                    <div style={{ ...alignmentStyle, gap: '5px' }}>
                        {eventIdStr === "5" && <img src={Icon.YELLOW_CARD} style={{ width: '9px' }} alt="黄牌" />}
                        {eventIdStr === "6" && <img src={Icon.RED_CARD} style={{ width: '9px' }} alt="红牌" />}
                        {eventIdStr === "9" && <img src={Icon.PENALTY_NOT_SCORED} style={{ width: '9px' }} alt="点球未进" />}
                        <span className="event-player-name">{item.userName}</span>
                    </div>
                )}

                {isSubstitution && (
                    <div style={{ width: '100%' }}>
                        {item.memberId?.split(",").map((mid) => {
                            const member = memberListInfo.find(m => String(m.id) === String(mid));
                            return member ? (
                                <div key={`up-${mid}`} className="event-sub-row" style={{ ...alignmentStyle, gap: '3px' }}>
                                    <img src={Icon.UP_ICON} style={{ width: '9px' }} alt="换上" />
                                    <span>{member.name}</span>
                                </div>
                            ) : null;
                        })}
                        {String(getUpDownMemberId(item)).split(",").filter(Boolean).map((mid) => {
                            const member = memberListInfo.find(m => String(m.id) === String(mid));
                            return member ? (
                                <div key={`down-${mid}`} className="event-sub-row" style={{ ...alignmentStyle, gap: '3px' }}>
                                    <img src={Icon.DOWN_ICON} style={{ width: '9px' }} alt="换下" />
                                    <span>{member.name}</span>
                                </div>
                            ) : null;
                        })}
                    </div>
                )}
            </div>
        );
    };

    const formatTime = (seconds) => {
        // 将总秒数转换为分钟
        const minutes = Math.floor(seconds / 60);
        // 获取剩余的秒数
        const remainingSeconds = seconds % 60;

        // 格式化分钟和秒数为两位数
        const formattedMinutes = minutes.toString().padStart(2, '0');
        const formattedSeconds = remainingSeconds.toString().padStart(2, '0');

        // 返回格式化的时间字符串
        return `${formattedMinutes}:${formattedSeconds}`;
    }

    let team1Goals = 0;
    let team2Goals = 0;

    // 活动赛的外战队没有普通队员名单，使用队长记录中的代表球员作为该队唯一可选对象。
    const getExternalRepresentative = (teamNo) => {
        if (Number(gameInfo?.gameType) !== 4) return null;
        const captainId = teamNo === 1 ? gameInfo?.greenCaptainId : gameInfo?.redCaptainId;
        const representative = (memberListInfo || []).find(item => String(item.id) === String(captainId));
        return Number(representative?.tempMember ?? representative?.temp_member) === 2
            ? { ...representative, gameTeam: teamNo, gameType: 0 }
            : null;
    };
    const getTeamPlayers = (teamNo) => {
        const players = (memberListInfo || []).filter(m => Number(m.gameType) === 0 && Number(m.gameTeam) === teamNo);
        const representative = getExternalRepresentative(teamNo);
        if (representative && !players.some(player => String(player.id) === String(representative.id))) {
            players.push(representative);
        }
        return players;
    };
    const team1Players = getTeamPlayers(1);
    const team2Players = getTeamPlayers(2);
    const totalCourses = Math.max(Number(gameInfo?.gameCourse || 1), 1);
    const courseOptions = Array.from({ length: totalCourses }, (_, i) => ({ label: `第${i + 1}节`, value: i + 1 }));

    const getMemberById = (memberId) => memberListInfo?.find(m => String(m.id) === String(memberId));
    const getMemberTeam = (memberId) => {
        const member = getMemberById(memberId);
        if (member?.gameTeam) return Number(member.gameTeam);
        if (team1Players.some(item => String(item.id) === String(memberId))) return 1;
        if (team2Players.some(item => String(item.id) === String(memberId))) return 2;
        return 0;
    };
    const getTempMemberValue = useCallback((member) => member?.tempMember ?? member?.temp_member, []);
    const getTempMemberMeta = useCallback((member) => {
        const tempMember = getTempMemberValue(member);
        if (tempMember === null || tempMember === undefined || tempMember === '') return { label: '常驻', order: 0 };
        if (Number(tempMember) === 3) return { label: '非正式', order: 1 };
        if (Number(tempMember) === 4) return { label: '离队', order: 2 };
        if (Number(tempMember) === 1) return { label: '临时', order: 3 };
        return { label: '其他', order: 4 };
    }, [getTempMemberValue]);
    const getEventLabel = (eventId) => {
        const id = String(eventId);
        if (id === HAPPY_EVENT_ID) return '赛外事件';
        return goalTypeOptions.find(item => item.value === id)?.label
            || cardTypeOptions.find(item => item.value === id)?.label
            || '赛况记录';
    };

    const getAssistOptions = (memberId) => {
        const team = getMemberTeam(memberId);
        const scorer = getMemberById(memberId);
        // 外战代表代表整支队伍，不应同时作为自己的助攻人。
        if (Number(scorer?.tempMember ?? scorer?.temp_member) === 2) return [{ label: '无助攻', value: '1' }];
        const currentTeamPlayers = team === 1 ? team1Players : team === 2 ? team2Players : [];
        return [{ label: '无助攻', value: '1' }, ...currentTeamPlayers.map(m => ({ label: m.name, value: m.id }))];
    };

    const normalizeHappyMember = useCallback((item) => {
        const name = item?.name || item?.memberName || item?.userName || '队员';
        const typeMeta = getTempMemberMeta(item);
        return {
            id: item.id,
            name,
            value: item.id,
            label: name,
            typeLabel: typeMeta.label,
            typeOrder: typeMeta.order,
            searchText: `${name} ${typeMeta.label}`
        };
    }, [getTempMemberMeta]);

    const selectableEventMembers = useMemo(() => {
        const mergedMap = new Map();
        (memberListInfo || []).forEach(item => {
            if (item?.id) mergedMap.set(String(item.id), item);
        });
        (allMemberOptions || []).forEach(item => {
            if (item?.id && !mergedMap.has(String(item.id))) mergedMap.set(String(item.id), item);
        });
        return Array.from(mergedMap.values())
            .filter(item => item?.id && item?.name)
            .map(normalizeHappyMember)
            // 赛外事件的关联队员选择完全沿用上传影像的队员排序逻辑。
            .filter(item => item.typeOrder <= 4)
            .sort((a, b) => a.typeOrder - b.typeOrder || String(a.name).localeCompare(String(b.name), 'zh-Hans-CN'));
    }, [allMemberOptions, memberListInfo, normalizeHappyMember]);

    const filteredHappyMembers = useMemo(() => {
        const keyword = happyMemberSearch.trim().toLowerCase();
        return selectableEventMembers.filter(item => {
            if (!keyword) return true;
            return item.searchText.toLowerCase().includes(keyword);
        });
    }, [happyMemberSearch, selectableEventMembers]);

    const getEventMemberNames = (memberIds) => {
        const ids = String(memberIds || '').split(',').filter(Boolean);
        return ids.map(id => selectableEventMembers.find(item => String(item.id) === String(id))?.name || getMemberById(id)?.name).filter(Boolean).join('、');
    };

    const loadAllMembersForHappy = () => {
        if (allMemberOptions.length > 0) return;
        const yearNum = new Date().getFullYear();
        axios.get(API_ROUTES.soccer_list, {
            params: { year: yearNum, type: 1 }
        }).then((response) => {
            const data = response.data?.data || [];
            // 成员接口是分组结构，这里拍平成赛外事件选择弹窗的数据源。
            const records = Array.isArray(data) && data[0]?.items
                ? data.flatMap(group => group.items || [])
                : data;
            setAllMemberOptions(Array.isArray(records) ? records : []);
        }).catch(() => {
            Toast.show({ icon: 'fail', content: '队员列表加载失败' });
        });
    };

    // 新增记录统一走底部弹窗，避免连续 Dialog 造成流程割裂。
    const openAddRecord = () => {
        setAddRecordType('');
        setAddRecordData({
            eventId: '13',
            memberId: '',
            assistMemberId: '1',
            type: 1,
            minuteTime: 1
        });
        setAddRecordVisible(true);
    };

    const chooseAddType = (type) => {
        setAddRecordType(type);
        if (type === 'goal') {
            setAddRecordData({ eventId: '13', memberId: '', assistMemberId: '1', type: 1, minuteTime: 1 });
            return;
        }
        if (type === 'card') {
            setAddRecordData({ eventId: '5', memberId: '', type: 1, minuteTime: 1 });
            return;
        }
        if (type === 'substitution') {
            setAddRecordData({ eventId: SUBSTITUTION_EVENT_ID, gameTeam: 1, memberId: [], upDownMemberId: [], type: 1, minuteTime: 1 });
            return;
        }
        // 赛外事件固定写入 eventId=22，仅保存纪念信息与关联人员，不参与比分统计。
        setAddRecordData({ eventId: HAPPY_EVENT_ID, memberId: [], eventInfo: '' });
        loadAllMembersForHappy();
    };

    const updateAddRecord = (updates) => {
        setAddRecordData(prev => ({ ...prev, ...updates }));
    };

    const closeAddRecord = () => {
        setAddRecordVisible(false);
        setAddRecordType('');
        setAddRecordData({});
        setHappyMemberPopupVisible(false);
        setHappyMemberSearch('');
        setHappyMemberDraft([]);
        setHappyMemberPickerTarget('add');
    };

    const parseHappyMemberIds = (value) => {
        if (Array.isArray(value)) return value.map(String).filter(Boolean);
        return String(value || '').split(',').map(id => id.trim()).filter(Boolean);
    };

    const getSelectedHappyMemberIds = () => parseHappyMemberIds(addRecordData.memberId);
    const getSelectedEditHappyMemberIds = () => parseHappyMemberIds(editRecordData.memberId);

    const toggleHappyMember = (memberId) => {
        const id = String(memberId);
        setHappyMemberDraft(prev => {
            const selectedIds = Array.isArray(prev) ? prev.map(String) : [];
            return selectedIds.includes(id)
                ? selectedIds.filter(item => item !== id)
                : [...selectedIds, id];
        });
    };

    const openHappyMemberPicker = (target = 'add') => {
        loadAllMembersForHappy();
        setHappyMemberSearch('');
        setHappyMemberPickerTarget(target);
        setHappyMemberDraft(target === 'edit' ? getSelectedEditHappyMemberIds() : getSelectedHappyMemberIds().map(String));
        setHappyMemberPopupVisible(true);
    };

    const confirmHappyMemberPicker = () => {
        if (happyMemberPickerTarget === 'edit') {
            updateEditRecord({ memberId: happyMemberDraft.join(',') });
        } else {
            updateAddRecord({ memberId: happyMemberDraft });
        }
        setHappyMemberPopupVisible(false);
        setHappyMemberSearch('');
    };

    const getHappyMemberNamesText = (ids) => parseHappyMemberIds(ids)
        .map(id => selectableEventMembers.find(item => String(item.id) === String(id))?.name)
        .filter(Boolean)
        .join('、');

    const selectedHappyMemberNames = () => getHappyMemberNamesText(addRecordData.memberId);

    const submitAddRecord = async () => {
        if (!addRecordType) return;
        if (addRecordType === 'happy' && !String(addRecordData.eventInfo || '').trim()) {
            Toast.show({ icon: 'fail', content: '请输入赛外内容' });
            return;
        }
        if (addRecordType === 'happy' && (!Array.isArray(addRecordData.memberId) || addRecordData.memberId.length === 0)) {
            Toast.show({ icon: 'fail', content: '请选择关联队员' });
            return;
        }
        if (addRecordType !== 'happy' && !addRecordData.memberId) {
            Toast.show({ icon: 'fail', content: '请选择球员' });
            return;
        }

        Toast.show({ icon: 'loading', content: '正在添加', duration: 0 });
        try {
            const payload = {
                gameId: gameInfo.id,
                eventId: addRecordData.eventId,
            };
            if (addRecordType === 'happy') {
                payload.memberId = addRecordData.memberId.join(',');
                payload.eventInfo = String(addRecordData.eventInfo || '').trim();
            } else if (addRecordType === 'substitution') {
                const upIds = Array.isArray(addRecordData.memberId) ? addRecordData.memberId : String(addRecordData.memberId || '').split(',').filter(Boolean);
                const downIds = Array.isArray(addRecordData.upDownMemberId) ? addRecordData.upDownMemberId : String(addRecordData.upDownMemberId || '').split(',').filter(Boolean);
                if (!addRecordData.gameTeam || !upIds.length || !downIds.length) {
                    Toast.show({ icon: 'fail', content: '请选择队伍和上下场人员' });
                    return;
                }
                if (upIds.length !== downIds.length) {
                    Toast.show({ icon: 'fail', content: '上下场人数需相等' });
                    return;
                }
                payload.memberId = upIds.join(',');
                payload.upDownMemberId = downIds.join(',');
                payload.type = addRecordData.type || 1;
                payload.minuteTime = addRecordData.minuteTime || 1;
            } else {
                payload.memberId = addRecordData.memberId;
                payload.type = addRecordData.type || 1;
                payload.minuteTime = addRecordData.minuteTime || 1;
            }
            if (addRecordType === 'goal') {
                payload.assistMemberId = addRecordData.assistMemberId || '1';
            }
            const res = await axios.post(API_ROUTES.game_addEventInfo, payload);
            if (res.data.code === 200) {
                Toast.show({ icon: 'success', content: '添加成功' });
                getGameDetail?.();
                getEventInfo?.();
                closeAddRecord();
            } else {
                Toast.show({ icon: 'fail', content: res.data.msg });
            }
        } catch (error) {
            Toast.show({ icon: 'fail', content: '添加失败' });
        }
    };

    const eventPeriodMap = (() => {
        let period = 0;
        const periodMap = {};
        // 根据半场/节开始事件给普通赛况归属节数，保证编辑列表和赛况时间线一致。
        (eventInfo || [])
            .slice()
            .sort((a, b) => new Date(a.currentTime || 0) - new Date(b.currentTime || 0))
            .forEach(item => {
                if (String(item.eventId) === '8') {
                    period += 1;
                }
                periodMap[item.id] = Math.max(period, 1);
            });
        return periodMap;
    })();

    const editableEvents = (() => {
        const keyword = eventSearchValue.trim().toLowerCase();
        return (eventInfo || [])
            .filter(item => GOAL_RECORD_EVENT_IDS.includes(String(item.eventId)) || CARD_EVENT_IDS.includes(String(item.eventId)) || String(item.eventId) === SUBSTITUTION_EVENT_ID)
            .filter(item => showSubstitutionRecords || String(item.eventId) !== SUBSTITUTION_EVENT_ID)
            .filter(item => {
                if (!keyword) return true;
                const text = [
                    item.eventName,
                    item.eventInfo,
                    getEventLabel(item.eventId),
                    item.userName,
                    item.assistMemberName,
                    item.eventTimeName,
                    item.currentTime
                ].filter(Boolean).join(' ').toLowerCase();
                return text.includes(keyword);
            })
            .map(item => ({ ...item, eventPeriod: eventPeriodMap[item.id] || 1 }))
            .sort((a, b) => {
                if (Number(a.eventPeriod) !== Number(b.eventPeriod)) {
                    return Number(a.eventPeriod) - Number(b.eventPeriod);
                }
                // 上下场可能发生在上一节结束、下一节开始之间，eventTime 为空或为 0；列表必须按真实发生时间排序。
                const aCurrentTime = new Date(String(a.currentTime || '').replace(/-/g, '/')).getTime() || 0;
                const bCurrentTime = new Date(String(b.currentTime || '').replace(/-/g, '/')).getTime() || 0;
                if (aCurrentTime !== bCurrentTime) return aCurrentTime - bCurrentTime;
                return Number(a.eventTime || 0) - Number(b.eventTime || 0);
            });
    })();

    const editableEventGroups = (() => {
        const groupMap = {};
        editableEvents.forEach(item => {
            const period = item.eventPeriod || 1;
            groupMap[period] = [...(groupMap[period] || []), item];
        });
        return Object.entries(groupMap).map(([period, records]) => ({
            period: Number(period),
            records
        }));
    })();

    const openEditRecord = () => {
        setEditingEvent(null);
        setEditRecordData({});
        setEventSearchValue('');
        setShowSubstitutionRecords(true);
        setEditRecordVisible(true);
    };

    // 编辑记录先展示可修改事件列表，再进入单条记录的修改/删除表单。
    const selectEditingEvent = (item) => {
        setEditingEvent(item);
        setEditRecordData({
            id: item.id,
            eventId: String(item.eventId),
            memberId: item.memberId,
            upDownMemberId: getUpDownMemberId(item),
            gameTeam: Number(item.gameTeam) || getMemberTeam(String(item.memberId || '').split(',')[0]),
            assistMemberId: item.assistMemberId || '1',
            eventInfo: item.eventInfo || ''
        });
    };

    const openHappyEventEdit = (item) => {
        if (memberInfo?.admin !== 1) return;
        loadAllMembersForHappy();
        // 管理员点击赛外事件卡片时直接进入修改界面，复用现有赛况修改接口。
        setEditingEvent(item);
        setEditRecordData({
            id: item.id,
            eventId: HAPPY_EVENT_ID,
            memberId: item.memberId || '',
            eventInfo: item.eventInfo || ''
        });
        setEditRecordVisible(true);
    };

    const updateEditRecord = (updates) => {
        setEditRecordData(prev => ({ ...prev, ...updates }));
    };

    const closeEditRecord = () => {
        setEditRecordVisible(false);
        setEditingEvent(null);
        setEditRecordData({});
        setEventSearchValue('');
        setHappyMemberPopupVisible(false);
        setHappyMemberSearch('');
        setHappyMemberDraft([]);
        setHappyMemberPickerTarget('add');
    };

    const submitEditRecord = async () => {
        if (!editingEvent || !editRecordData.eventId) {
            Toast.show({ icon: 'fail', content: '请完善赛况信息' });
            return;
        }
        if (String(editRecordData.eventId) === HAPPY_EVENT_ID) {
            if (!String(editRecordData.eventInfo || '').trim()) {
                Toast.show({ icon: 'fail', content: '请输入赛外内容' });
                return;
            }
            if (!String(editRecordData.memberId || '').trim()) {
                Toast.show({ icon: 'fail', content: '请选择关联队员' });
                return;
            }
        } else if (String(editRecordData.eventId) === SUBSTITUTION_EVENT_ID) {
            const upIds = String(editRecordData.memberId || '').split(',').map(item => item.trim()).filter(Boolean);
            const downIds = String(editRecordData.upDownMemberId || '').split(',').map(item => item.trim()).filter(Boolean);
            if (!editRecordData.gameTeam || !upIds.length || !downIds.length) {
                Toast.show({ icon: 'fail', content: '请选择队伍和上下场人员' });
                return;
            }
            if (upIds.length !== downIds.length) {
                Toast.show({ icon: 'fail', content: '上下场人数需相等' });
                return;
            }
        } else if (!editRecordData.memberId) {
            Toast.show({ icon: 'fail', content: '请选择球员' });
            return;
        }
        Toast.show({ icon: 'loading', content: '正在保存', duration: 0 });
        try {
            // 选择器内部允许使用数组，多人上下场提交前统一转换为逗号分隔字符串。
            const normalizedMemberId = Array.isArray(editRecordData.memberId)
                ? editRecordData.memberId.join(',')
                : String(editRecordData.memberId || '');
            const normalizedUpDownMemberId = Array.isArray(editRecordData.upDownMemberId)
                ? editRecordData.upDownMemberId.join(',')
                : String(editRecordData.upDownMemberId || '');
            const payload = {
                id: editingEvent.id,
                memberId: normalizedMemberId,
                eventId: editRecordData.eventId
            };
            if (String(editRecordData.eventId) === SUBSTITUTION_EVENT_ID) {
                payload.upDownMemberId = normalizedUpDownMemberId;
            }
            if (String(editRecordData.eventId) === HAPPY_EVENT_ID) {
                payload.eventInfo = String(editRecordData.eventInfo || '').trim();
            }
            if (GOAL_RECORD_EVENT_IDS.includes(String(editRecordData.eventId))) {
                payload.assistMemberId = editRecordData.assistMemberId || '1';
            }
            const res = await axios.post(API_ROUTES.game_updateEventInfo, payload);
            if (res.data.code === 200) {
                Toast.show({ icon: 'success', content: '修改成功' });
                getGameDetail?.();
                getEventInfo?.();
                closeEditRecord();
            } else {
                Toast.show({ icon: 'fail', content: res.data.msg });
            }
        } catch (error) {
            Toast.show({ icon: 'fail', content: '修改失败' });
        }
    };

    const deleteEditingRecord = () => {
        if (!editingEvent) return;
        Dialog.confirm({
            title: '删除这条赛况？',
            content: `${getEventLabel(editingEvent.eventId)} · ${editingEvent.userName || '未记录球员'}`,
            confirmText: '删除',
            cancelText: '取消',
            onConfirm: async () => {
                Toast.show({ icon: 'loading', content: '正在删除', duration: 0 });
                try {
                    const res = await axios.post(API_ROUTES.game_deleteEventInfo, { id: editingEvent.id });
                    if (res.data.code === 200) {
                        Toast.show({ icon: 'success', content: '删除成功' });
                        getGameDetail?.();
                        getEventInfo?.();
                        closeEditRecord();
                    } else {
                        Toast.show({ icon: 'fail', content: res.data.msg });
                    }
                } catch (error) {
                    Toast.show({ icon: 'fail', content: '删除失败' });
                }
            }
        });
    };

    const renderTeamMemberSelectors = (formData, onChange, includeAssist = false) => {
        const selectedTeam = getMemberTeam(formData.memberId);
        return (
            <>
                <div className="record-form-section-title">选择球员</div>
                <div className="record-team-block">
                    <div className="record-team-title">{gameInfo?.greenName || 'A队'}</div>
                    <Selector
                        options={team1Players.map(m => ({ label: m.name, value: m.id }))}
                        value={selectedTeam === 1 && formData.memberId ? [formData.memberId] : []}
                        onChange={arr => {
                            if (!arr.length) return;
                            onChange({ memberId: arr[0], assistMemberId: '1' });
                        }}
                        columns={3}
                    />
                </div>
                <div className="record-team-block">
                    <div className="record-team-title">{gameInfo?.redName || 'B队'}</div>
                    <Selector
                        options={team2Players.map(m => ({ label: m.name, value: m.id }))}
                        value={selectedTeam === 2 && formData.memberId ? [formData.memberId] : []}
                        onChange={arr => {
                            if (!arr.length) return;
                            onChange({ memberId: arr[0], assistMemberId: '1' });
                        }}
                        columns={3}
                    />
                </div>
                {includeAssist && (
                    <>
                        <div className="record-form-section-title">助攻人员</div>
                        <Selector
                            options={getAssistOptions(formData.memberId)}
                            value={[formData.assistMemberId || '1']}
                            onChange={arr => arr.length && onChange({ assistMemberId: arr[0] })}
                            columns={3}
                        />
                    </>
                )}
            </>
        );
    };

    // 上下场两组选人共用当前队伍成员，并互相排除已选人员，避免同一人同时上下场。
    const renderSubstitutionSelectors = (formData, onChange) => {
        const teamPlayers = Number(formData.gameTeam) === 2 ? team2Players : team1Players;
        const upIds = Array.isArray(formData.memberId) ? formData.memberId.map(String) : String(formData.memberId || '').split(',').filter(Boolean);
        const downIds = Array.isArray(formData.upDownMemberId) ? formData.upDownMemberId.map(String) : String(formData.upDownMemberId || '').split(',').filter(Boolean);
        const options = teamPlayers.map(member => ({ label: member.name, value: String(member.id) }));
        return (
            <>
                <div className="record-form-section-title">操作队伍</div>
                <Selector
                    options={[{ label: gameInfo?.greenName || 'A队', value: 1 }, { label: gameInfo?.redName || 'B队', value: 2 }]}
                    value={formData.gameTeam ? [Number(formData.gameTeam)] : []}
                    onChange={arr => arr.length && onChange({ gameTeam: Number(arr[0]), memberId: [], upDownMemberId: [] })}
                    columns={2}
                />
                <div className="record-form-section-title">上场人员</div>
                <Selector
                    multiple
                    options={options.filter(option => !downIds.includes(String(option.value)))}
                    value={upIds}
                    onChange={arr => onChange({ memberId: arr.map(String) })}
                    columns={3}
                />
                <div className="record-form-section-title">下场人员</div>
                <Selector
                    multiple
                    options={options.filter(option => !upIds.includes(String(option.value)))}
                    value={downIds}
                    onChange={arr => onChange({ upDownMemberId: arr.map(String) })}
                    columns={3}
                />
            </>
        );
    };

    const renderAddRecordForm = () => {
        if (!addRecordType) {
            return (
                <div className="record-type-grid">
                    <button type="button" className="record-type-card goal" onClick={() => chooseAddType('goal')}>
                        <span className="record-type-icon">⚽</span>
                        <span className="record-type-title">添加进球</span>
                        <span className="record-type-desc">进球方式、球员、助攻、时间</span>
                    </button>
                    <button type="button" className="record-type-card card" onClick={() => chooseAddType('card')}>
                        <span className="record-type-icon">🟨</span>
                        <span className="record-type-title">添加吃牌</span>
                        <span className="record-type-desc">黄牌、红牌、球员与时间</span>
                    </button>
                    <button type="button" className="record-type-card substitution" onClick={() => chooseAddType('substitution')}>
                        <span className="record-type-icon">🔁</span>
                        <span className="record-type-title">添加上下场</span>
                        <span className="record-type-desc">选择队伍及多人上下场人员</span>
                    </button>
                    <button type="button" className="record-type-card happy" onClick={() => chooseAddType('happy')}>
                        <span className="record-type-icon">🎉</span>
                        <span className="record-type-title">添加赛外事件</span>
                        <span className="record-type-desc">生日、纪念、荣誉，可继续上传影像</span>
                    </button>
                </div>
            );
        }

        if (addRecordType === 'happy') {
            return (
                <div className="record-form">
                    <div className="record-form-section-title">赛外内容</div>
                    <div className="record-happy-quick-row">
                        {happyQuickOptions.map(option => (
                            <button
                                key={option}
                                type="button"
                                className={`record-happy-quick ${addRecordData.eventInfo === option ? 'active' : ''}`}
                                onClick={() => updateAddRecord({ eventInfo: option })}
                            >
                                {option}
                            </button>
                        ))}
                    </div>
                    <Input
                        className="record-happy-input"
                        placeholder="快捷选项或手动输入，例如：xx生日"
                        value={addRecordData.eventInfo || ''}
                        onChange={value => updateAddRecord({ eventInfo: value })}
                    />
                    <div className="record-form-section-title">关联队员</div>
                    <button type="button" className="record-happy-member-field" onClick={openHappyMemberPicker}>
                        <span>{selectedHappyMemberNames() || '请选择关联队员'}</span>
                        <em>{getSelectedHappyMemberIds().length ? `${getSelectedHappyMemberIds().length} 人` : '选择'}</em>
                    </button>
                </div>
            );
        }

        if (addRecordType === 'substitution') {
            return (
                <div className="record-form">
                    {renderSubstitutionSelectors(addRecordData, updateAddRecord)}
                    <div className="record-form-section-title">比赛节数</div>
                    <Selector options={courseOptions} value={[addRecordData.type || 1]} onChange={arr => arr.length && updateAddRecord({ type: arr[0] })} columns={Math.min(totalCourses, 4)} />
                    <div className="record-form-section-title">发生时间</div>
                    <div className="record-time-stepper"><span>第</span><Stepper value={addRecordData.minuteTime || 1} min={1} onChange={value => updateAddRecord({ minuteTime: value })} /><span>分钟</span></div>
                </div>
            );
        }

        const isGoal = addRecordType === 'goal';
        return (
            <div className="record-form">
                <div className="record-form-section-title">{isGoal ? '进球方式' : '吃牌方式'}</div>
                <Selector
                    options={isGoal ? goalTypeOptions : cardTypeOptions}
                    value={[addRecordData.eventId]}
                    onChange={arr => arr.length && updateAddRecord({ eventId: arr[0] })}
                    columns={2}
                />
                {renderTeamMemberSelectors(addRecordData, updateAddRecord, isGoal)}
                <div className="record-form-section-title">比赛节数</div>
                <Selector
                    options={courseOptions}
                    value={[addRecordData.type || 1]}
                    onChange={arr => arr.length && updateAddRecord({ type: arr[0] })}
                    columns={Math.min(totalCourses, 4)}
                />
                <div className="record-form-section-title">{isGoal ? '进球时间' : '吃牌时间'}</div>
                <div className="record-time-stepper">
                    <span>第</span>
                    <Stepper
                        value={addRecordData.minuteTime || 1}
                        min={1}
                        onChange={value => updateAddRecord({ minuteTime: value })}
                    />
                    <span>分钟</span>
                </div>
            </div>
        );
    };

    const renderEventListItem = (item) => {
        if (String(item.eventId) === SUBSTITUTION_EVENT_ID) {
            const memberNames = (ids) => String(ids || '').split(',').map(id => getMemberById(id)?.name).filter(Boolean).join('、') || '未记录人员';
            return (
                <button key={item.id} type="button" className="record-event-row substitution" onClick={() => selectEditingEvent(item)}>
                    <span className="record-event-mark substitution">🔁</span>
                    <span className="record-event-main"><strong>上下场</strong><em>上场：{memberNames(item.memberId)} · 下场：{memberNames(getUpDownMemberId(item))}</em></span>
                    <span className="record-event-time">{item.eventTimeName || formatTime(item.eventTime || 0)}</span>
                </button>
            );
        }
        const isGoal = isGoalRecordEvent(item.eventId);
        return (
            <button
                key={item.id}
                type="button"
                className="record-event-row"
                onClick={() => selectEditingEvent(item)}
            >
                <span className={`record-event-mark ${isGoal ? 'goal' : 'card'}`}>
                    {isGoal ? '⚽' : (String(item.eventId) === '6' ? '🟥' : '🟨')}
                </span>
                <span className="record-event-main">
                    <strong>{getEventLabel(item.eventId)}</strong>
                    <em>
                        {item.userName || '未记录球员'}
                        {item.assistMemberName ? ` · 助攻 ${item.assistMemberName}` : ''}
                    </em>
                </span>
                <span className="record-event-time">{item.eventTimeName || formatTime(item.eventTime || 0)}</span>
            </button>
        );
    };

    const renderEditRecordContent = () => {
        if (!editingEvent) {
            return (
                <>
                    <div className="record-search-wrap">
                        <SearchBar
                            placeholder="搜索球员、事件或时间"
                            value={eventSearchValue}
                            onChange={setEventSearchValue}
                        />
                        <button
                            type="button"
                            className={`record-substitution-filter ${showSubstitutionRecords ? 'active' : ''}`}
                            onClick={() => setShowSubstitutionRecords(value => !value)}
                        >
                            {showSubstitutionRecords ? '隐藏上下场' : '显示上下场'}
                        </button>
                    </div>
                    <div className="record-event-list">
                        {editableEventGroups.length > 0 ? editableEventGroups.map(group => (
                            <div key={group.period} className="record-event-period">
                                <div className="record-event-period-title">
                                    <span>第 {group.period} 节</span>
                                    <em>{group.records.length} 条</em>
                                </div>
                                <div className="record-event-period-list">
                                    {group.records.map(renderEventListItem)}
                                </div>
                            </div>
                        )) : (
                            <Empty description="暂无可编辑赛况" imageStyle={{ width: 80 }} />
                        )}
                    </div>
                </>
            );
        }

        if (String(editRecordData.eventId) === HAPPY_EVENT_ID) {
            return (
                <div className="record-form">
                    <button type="button" className="record-back-button" onClick={() => setEditingEvent(null)}>返回事件列表</button>
                    <div className="record-edit-summary happy">
                        <span>赛外事件</span>
                        <strong>{editingEvent.currentTime || '比赛纪念'}</strong>
                    </div>
                    <div className="record-form-section-title">赛外内容</div>
                    <div className="record-happy-quick-row">
                        {happyQuickOptions.map(option => (
                            <button
                                key={option}
                                type="button"
                                className={`record-happy-quick ${editRecordData.eventInfo === option ? 'active' : ''}`}
                                onClick={() => updateEditRecord({ eventInfo: option })}
                            >
                                {option}
                            </button>
                        ))}
                    </div>
                    <Input
                        className="record-happy-input"
                        placeholder="填写赛外内容，例如：xx生日"
                        value={editRecordData.eventInfo || ''}
                        onChange={value => updateEditRecord({ eventInfo: value })}
                    />
                    <div className="record-form-section-title">关联队员</div>
                    <button type="button" className="record-happy-member-field" onClick={() => openHappyMemberPicker('edit')}>
                        <span>{getHappyMemberNamesText(editRecordData.memberId) || '请选择关联队员'}</span>
                        <em>{getSelectedEditHappyMemberIds().length ? `${getSelectedEditHappyMemberIds().length} 人` : '选择'}</em>
                    </button>
                </div>
            );
        }

        const isGoal = isGoalRecordEvent(editRecordData.eventId);
        if (String(editRecordData.eventId) === SUBSTITUTION_EVENT_ID) {
            return (
                <div className="record-form">
                    <button type="button" className="record-back-button" onClick={() => setEditingEvent(null)}>返回事件列表</button>
                    <div className="record-edit-summary substitution"><span>上下场记录</span><strong>{editingEvent.eventTimeName || formatTime(editingEvent.eventTime || 0)}</strong></div>
                    {renderSubstitutionSelectors(editRecordData, updateEditRecord)}
                    <div className="record-edit-danger"><span>删除后会同步重算所有队员在场时间。</span><Button size="small" color="danger" onClick={deleteEditingRecord}>删除记录</Button></div>
                </div>
            );
        }
        return (
            <div className="record-form">
                <button type="button" className="record-back-button" onClick={() => setEditingEvent(null)}>返回事件列表</button>
                <div className="record-edit-summary">
                    <span>{isGoal ? '进球记录' : '吃牌记录'}</span>
                    <strong>{editingEvent.eventTimeName || formatTime(editingEvent.eventTime || 0)}</strong>
                </div>
                <div className="record-form-section-title">{isGoal ? '进球方式' : '吃牌方式'}</div>
                <Selector
                    options={isGoal ? goalTypeOptions : cardTypeOptions}
                    value={[editRecordData.eventId]}
                    onChange={arr => arr.length && updateEditRecord({ eventId: arr[0] })}
                    columns={2}
                />
                {renderTeamMemberSelectors(editRecordData, updateEditRecord, isGoal)}
                <div className="record-edit-danger">
                    <span>删除后会同步移除这条赛况记录。</span>
                    <Button size="small" color="danger" onClick={deleteEditingRecord}>删除记录</Button>
                </div>
            </div>
        );
    };

    const parseEventDate = (value) => {
        if (!value) return null;
        if (value instanceof Date) return value;
        const raw = String(value).trim();
        // 兼容接口返回的空格格式和 ISO 格式时间。
        const normalized = raw.includes('T') ? raw : raw.replace(/-/g, '/');
        const parsed = new Date(normalized);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const getPauseSecondsBetween = (startDate, endDate) => {
        if (!startDate || !endDate || endDate <= startDate) return 0;
        const pauseEvents = (eventInfo || [])
            .filter(event => String(event.eventId) === '18')
            .map(event => parseEventDate(event.currentTime))
            .filter(Boolean)
            .sort((a, b) => a - b);
        let pauseSeconds = 0;
        for (let index = 0; index < pauseEvents.length; index += 2) {
            const pauseStart = new Date(Math.max(startDate.getTime(), pauseEvents[index].getTime()));
            const pauseEnd = new Date(Math.min(endDate.getTime(), (pauseEvents[index + 1] || endDate).getTime()));
            if (pauseEnd > pauseStart) pauseSeconds += Math.floor((pauseEnd - pauseStart) / 1000);
        }
        return pauseSeconds;
    };

    const startAndEndInfo = (item) => {
        const endEvents = eventInfo.filter(event => String(event.eventId) === "7");
        const startEvents = eventInfo.filter(event => String(event.eventId) === "8");
        if (String(item.eventId) === "7") {
            const endIndex = endEvents.findIndex(end => end.id === item.id);
            if (endIndex >= 0 && endIndex < startEvents.length) {
                const startTime = parseEventDate(startEvents[endIndex].currentTime);
                const endTime = parseEventDate(item.currentTime);
                if (startTime && endTime) {
                    // 单节时长按实际比赛时间计算，扣除本节所有暂停区间。
                    const wallSeconds = Math.max(Math.floor((endTime - startTime) / 1000), 0);
                    const activeSeconds = Math.max(wallSeconds - getPauseSecondsBetween(startTime, endTime), 0);
                    return `第 ${endIndex + 1} 半场结束 ${Math.floor(activeSeconds / 60)}'`;
                }
            }
        } else if (String(item.eventId) === "8") {
            const startIndex = startEvents.findIndex(start => start.id === item.id);
            return `第 ${startIndex + 1} 半场开始`;
        } else {
            return item.eventInfo ? item.eventInfo : item.eventName
        }
    }

    const happyEvents = (eventInfo || []).filter(item => String(item.eventId) === HAPPY_EVENT_ID);
    const normalEvents = (eventInfo || []).filter(item => String(item.eventId) !== HAPPY_EVENT_ID);

    const renderHappyEventCard = (item) => {
        const eventMediaList = eventAlbumMap[String(item.id)] || [];
        const hasEventVideo = eventMediaList.some(media => getMediaType(media) === 2);
        const previewMediaList = eventMediaList.slice(0, 3);
        return (
            <div
                key={item.id}
                className={`timeline-happy-event-card ${eventMediaList.length > 0 ? 'has-media' : 'no-media'} ${memberInfo?.admin === 1 ? 'editable' : ''}`}
                role={memberInfo?.admin === 1 ? 'button' : undefined}
                onClick={() => openHappyEventEdit(item)}
            >
                <div className="timeline-happy-event-main">
                    <div className="timeline-happy-event-top">
                        <span>{item.eventInfo || '比赛赛外'}</span>
                    </div>
                    <div className="timeline-happy-event-subline">
                        <span className="timeline-happy-event-members">{getEventMemberNames(item.memberId) || item.userName || '未关联队员'}</span>
                        <button
                            type="button"
                            className="timeline-happy-event-upload"
                            onClick={(event) => {
                                event.stopPropagation();
                                navigate('/albumUpload', {
                                    state: {
                                        gameId: gameInfo?.id,
                                        gameName: gameInfo?.gameName,
                                        eventId: item.id,
                                        eventName: item.eventInfo || '赛外事件',
                                        memberId: item.memberId || ''
                                    }
                                });
                            }}
                        >
                            上传影像
                        </button>
                    </div>
                </div>
                {eventMediaList.length > 0 && (
                    <button
                        type="button"
                        className="timeline-happy-event-media"
                        onClick={(event) => {
                            event.stopPropagation();
                            openEventAlbum(item, eventMediaList);
                        }}
                    >
                        <span className={`timeline-happy-event-thumb-grid count-${previewMediaList.length}`}>
                            {previewMediaList.map((media, mediaIndex) => (
                                <span key={media.id || mediaIndex} className="timeline-happy-event-thumb">
                                    <img src={buildThumbUrl(media)} alt={getMediaType(media) === 2 ? '赛外视频缩略图' : '赛外图片缩略图'} />
                                    {getMediaType(media) === 2 && <i>▶</i>}
                                </span>
                            ))}
                        </span>
                        <em>{hasEventVideo ? '视频/图片' : '图片'} {eventMediaList.length}</em>
                    </button>
                )}
            </div>
        );
    };

    return (
        <div className="event-timeline" style={{ overflowX: 'hidden' }}>
            <div className="event-timeline-inner">
                {(memberInfo?.admin === 1 || others?.some(user => user.id === memberInfo.id)) && (
                    <div className="admin-controls-container">
                        <div className="admin-btn-premium" onClick={openEditRecord}>
                            <span>✏️ 编辑赛况</span>
                        </div>
                        <div className="admin-btn-premium admin-btn-add" onClick={openAddRecord}>
                            <span>➕ 添加记录</span>
                        </div>
                    </div>
                )}
                {happyEvents.length > 0 && (
                    <div className="timeline-happy-event-list">
                        {happyEvents.map(renderHappyEventCard)}
                    </div>
                )}
                <Grid columns={24} className="timeline-item status-item">
                    <Grid.Item span={24} style={{ display: 'flex', justifyContent: 'center' }}><div className="capsule-status-badge">🚀 比赛开始</div></Grid.Item>
                </Grid>
                {normalEvents.map((item, index) => {
                    const eventMediaList = eventAlbumMap[String(item.id)] || [];
                    // 赛况时间线优先展示视频封面，保证有视频的事件一眼可见。
                    const eventCoverMedia = eventMediaList.find(media => getMediaType(media) === 2) || eventMediaList[0];
                    const hasEventVideo = eventMediaList.some(media => getMediaType(media) === 2);
                    const eid = String(item.eventId);
                    if (["11", "12", "13", "14", "15", "16", "3", "19", "20", "21"].includes(eid)) {
                        if (Number(item.gameTeam) === 1) team1Goals += 1; else if (Number(item.gameTeam) === 2) team2Goals += 1;
                    } else if (eid === '4') {
                        if (Number(item.gameTeam) === 1) team2Goals += 1; else if (Number(item.gameTeam) === 2) team1Goals += 1;
                    }
                    const isSubstitution = ["17", "18"].includes(String(item.eventId));
                    const isStatusLabel = item.eventTime === null || String(item.eventId) === "18" || ["7", "8"].includes(String(item.eventId));
                    const isTeam1 = Number(item.gameTeam) === 1 ||
                        item.memberId?.split(",").some(mid => memberListInfo.find(m => String(m.id) === String(mid))?.gameTeam === 1) ||
                        String(getUpDownMemberId(item)).split(",").some(mid => memberListInfo.find(m => String(m.id) === String(mid))?.gameTeam === 1);
                    const isTeam2 = Number(item.gameTeam) === 2 ||
                        item.memberId?.split(",").some(mid => memberListInfo.find(m => String(m.id) === String(mid))?.gameTeam === 2) ||
                        String(getUpDownMemberId(item)).split(",").some(mid => memberListInfo.find(m => String(m.id) === String(mid))?.gameTeam === 2);
                    return (
                        <Grid columns={24} key={item.id} className={`timeline-item ${isStatusLabel ? 'status-item' : ''}`}>
                            <Grid.Item span={9} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '0 8px' }}>
                                {(isTeam1 && (isSubstitution || !isStatusLabel)) ? teamInfo(item, "left", team1Goals, team2Goals) : null}
                            </Grid.Item>
                            <Grid.Item span={6} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
                                <div className="event-center-stack">
                                    {isStatusLabel ? <div className="capsule-status-badge">{startAndEndInfo(item)}</div> : <div className="time-badge">{formatTime(item.eventTime)}</div>}
                                    {eventMediaList.length > 0 && (
                                        <button
                                            type="button"
                                            className="event-album-entry"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                openEventAlbum(item, eventMediaList);
                                            }}
                                        >
                                            <div className="event-album-thumb-frame">
                                                <img
                                                    src={buildThumbUrl(eventCoverMedia)}
                                                    alt={hasEventVideo ? '赛况视频缩略图' : '赛况图片缩略图'}
                                                />
                                                {hasEventVideo && <span className="event-album-play-mark">▶</span>}
                                            </div>
                                            <div className={`event-album-type-chip ${hasEventVideo ? 'video' : 'image'}`}>
                                                {hasEventVideo ? '视频' : '图片'} {eventMediaList.length}
                                            </div>
                                        </button>
                                    )}
                                </div>
                            </Grid.Item>
                            <Grid.Item span={9} style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', padding: '0 8px' }}>
                                {(isTeam2 && (isSubstitution || !isStatusLabel)) ? teamInfo(item, "right", team1Goals, team2Goals) : null}
                            </Grid.Item>
                        </Grid>
                    );
                })}
                {gameInfo?.status === 3 && (
                    <Grid columns={24} className="timeline-item status-item" style={{ marginTop: '24px' }}>
                        <Grid.Item span={24} style={{ display: 'flex', justifyContent: 'center' }}>
                            <div className="capsule-status-badge">🏁 比赛结束</div>
                        </Grid.Item>
                    </Grid>
                )}
            </div>
            <Popup
                visible={addRecordVisible}
                onMaskClick={closeAddRecord}
                onClose={closeAddRecord}
                bodyClassName="record-bottom-popup"
                bodyStyle={{ borderTopLeftRadius: 22, borderTopRightRadius: 22 }}
            >
                <div className="record-popup-shell">
                    <div className="record-popup-header">
                        <button type="button" onClick={addRecordType ? () => setAddRecordType('') : closeAddRecord}>
                            {addRecordType ? '上一步' : '取消'}
                        </button>
                        <div className="record-popup-title">{addRecordType ? (addRecordType === 'goal' ? '添加进球记录' : (addRecordType === 'card' ? '添加吃牌记录' : (addRecordType === 'substitution' ? '添加上下场记录' : '添加赛外事件'))) : '添加赛况记录'}</div>
                        <button type="button" className="record-popup-primary" disabled={!addRecordType} onClick={submitAddRecord}>保存</button>
                    </div>
                    <div className="record-popup-content">
                        {renderAddRecordForm()}
                    </div>
                </div>
            </Popup>

            <Popup
                visible={happyMemberPopupVisible}
                onMaskClick={() => setHappyMemberPopupVisible(false)}
                onClose={() => setHappyMemberPopupVisible(false)}
                bodyClassName="record-member-picker-popup"
                bodyStyle={{ borderTopLeftRadius: 22, borderTopRightRadius: 22 }}
            >
                <div className="record-member-picker-shell">
                    <div className="record-member-picker-head">
                        <button type="button" onClick={() => setHappyMemberPopupVisible(false)}>取消</button>
                        <strong>选择关联队员</strong>
                        <button type="button" className="primary" onClick={confirmHappyMemberPicker}>确认</button>
                    </div>
                    <div className="record-member-picker-search">
                        <SearchBar
                            placeholder="搜索队员姓名"
                            value={happyMemberSearch}
                            onChange={setHappyMemberSearch}
                        />
                    </div>
                    <div className="record-member-picker-content">
                        {filteredHappyMembers.length > 0 ? (
                            <div className="album-member-picker-grid record-member-grid">
                                {filteredHappyMembers.map(member => {
                                    const selected = happyMemberDraft.map(String).includes(String(member.id));
                                    return (
                                        <button
                                            key={member.id}
                                            type="button"
                                            className={`album-picker-option record-member-card ${selected ? 'active' : ''}`}
                                            onClick={() => toggleHappyMember(member.id)}
                                        >
                                            <div className="album-member-option-content">
                                                <strong className="album-member-option-name">{member.name}</strong>
                                                {member.typeLabel && (
                                                    <em className={`album-member-type-badge album-member-type-${member.typeOrder}`}>
                                                        {member.typeLabel}
                                                    </em>
                                                )}
                                            </div>
                                            <i>{selected ? '已选' : ''}</i>
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <Empty description="未找到队员" imageStyle={{ width: 76 }} />
                        )}
                    </div>
                </div>
            </Popup>

            <Popup
                visible={editRecordVisible}
                onMaskClick={closeEditRecord}
                onClose={closeEditRecord}
                bodyClassName="record-bottom-popup"
                bodyStyle={{ borderTopLeftRadius: 22, borderTopRightRadius: 22 }}
            >
                <div className="record-popup-shell">
                    <div className="record-popup-header">
                        <button type="button" onClick={closeEditRecord}>取消</button>
                        <div className="record-popup-title">{editingEvent ? '修改赛况记录' : '选择赛况记录'}</div>
                        <button type="button" className="record-popup-primary" disabled={!editingEvent} onClick={submitEditRecord}>保存</button>
                    </div>
                    <div className="record-popup-content">
                        {renderEditRecordContent()}
                    </div>
                </div>
            </Popup>

            <Popup
                visible={!!selectedEventAlbum}
                onMaskClick={() => setSelectedEventAlbum(null)}
                bodyStyle={{ borderTopLeftRadius: 20, borderTopRightRadius: 20, background: '#FFFFFF', maxHeight: '86vh', overflowY: 'auto' }}
            >
                {selectedEventAlbum && (
                    <div className="event-album-popup">
                        <div className="event-album-popup-head">
                            <div>
                                <div className="event-album-popup-kicker">EVENT MEDIA</div>
                                <div className="event-album-popup-title">{selectedEventAlbum.event?.eventName || selectedEventAlbum.event?.eventInfo || '赛况影像'}</div>
                            </div>
                            <button type="button" onClick={() => setSelectedEventAlbum(null)}>关闭</button>
                        </div>
                        {selectedEventMediaList.length > 0 ? (
                            <div className="event-album-media-list">
                                {selectedEventMediaList.map((media, mediaIndex) => (
                                    <div key={media.id || mediaIndex} className="event-album-media-card">
                                        {getMediaType(media) === 2 ? (
                                            <video
                                                className="event-album-media"
                                                src={getMediaUrl(media)}
                                                poster={buildThumbUrl(media)}
                                                controls
                                            />
                                        ) : (
                                            <img
                                                className="event-album-media"
                                                src={getMediaUrl(media)}
                                                alt={media.remark || '赛况图片'}
                                            />
                                        )}
                                        <div className="event-album-media-info">
                                            <span>{media.remark || (getMediaType(media) === 2 ? '视频记录' : '图片记录')}</span>
                                            {(media.tagName || media.tag_name) && <i>{media.tagName || media.tag_name}</i>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <Empty description="暂无影像" imageStyle={{ width: 80 }} />
                        )}
                    </div>
                )}
            </Popup>
        </div>
    )
}



export default EventInfo;
